import React, { useMemo } from 'react'
import './DashboardOverviewCalendar.style.css'

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const
const SUNDAY = 0
const SATURDAY = 6

export function formatDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

type DashboardOverviewCalendarProps = {
  viewMonth: Date // first day of the month to display
  onDateClick?: (dateString: string) => void
}

function DashboardOverviewCalendar({
  viewMonth,
  onDateClick,
}: DashboardOverviewCalendarProps): React.ReactElement {
  const todayKey = useMemo(() => formatDateKey(new Date()), [])

  const calendarGrid = useMemo(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const startDay = first.getDay()
    const daysInMonth = last.getDate()

    const prevMonth = month === 0 ? 11 : month - 1
    const prevYear = month === 0 ? year - 1 : year
    const prevLast = new Date(prevYear, prevMonth + 1, 0)
    const daysInPrevMonth = prevLast.getDate()

    const cells: { day: number; isCurrentMonth: boolean; dateKey: string }[] =
      []

    for (let i = 0; i < startDay; i++) {
      const d = daysInPrevMonth - startDay + 1 + i
      const cellDate = new Date(prevYear, prevMonth, d)
      cells.push({
        day: d,
        isCurrentMonth: false,
        dateKey: formatDateKey(cellDate),
      })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(year, month, d)
      cells.push({
        day: d,
        isCurrentMonth: true,
        dateKey: formatDateKey(cellDate),
      })
    }
    const remaining = 42 - cells.length
    for (let i = 1; i <= remaining; i++) {
      const cellDate = new Date(year, month + 1, i)
      cells.push({
        day: i,
        isCurrentMonth: false,
        dateKey: formatDateKey(cellDate),
      })
    }
    return cells
  }, [viewMonth])

  return (
    <div className="dashboard-overview-calendar">
      <div className="dashboard-overview-calendar-day-headers">
        {DAY_LABELS.map((label, i) => (
          <span
            key={label + i}
            className={`dashboard-overview-calendar-day-name ${i === SUNDAY ? 'sunday' : ''} ${i === SATURDAY ? 'saturday' : ''}`}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="dashboard-overview-calendar-grid">
        {calendarGrid.map((cell) => {
          const isToday = cell.dateKey === todayKey
          return (
            <button
              key={cell.dateKey}
              type="button"
              className={`dashboard-overview-calendar-cell ${!cell.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
              onClick={() => onDateClick?.(cell.dateKey)}
            >
              {cell.day}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default DashboardOverviewCalendar
